import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { WizardState } from '@/components/business/create-business/wizard/types';

// ── Types ─────────────────────────────────────────────────────────────

export interface DraftSummary {
  draftId: string;
  displayName: string | null;
  progress: number;
  lastUpdated: string;
}

interface WizardDraftRow {
  id: string;
  user_id: string;
  display_name: string | null;
  current_step_id: string;
  setup_method: string | null;
  completed_steps: string[];
  skipped_steps: string[];
  wizard_state: Record<string, unknown>;
  version: number;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

export interface UpsertDraftPayload {
  id: string;
  displayName: string | null;
  currentStepId: string;
  setupMethod: string | null;
  completedSteps: string[];
  skippedSteps: string[];
  wizardState: WizardState;
}

// ── Query key factory ─────────────────────────────────────────────────

export const wizardDraftKeys = {
  all: ['wizard-drafts'] as const,
  lists: (userId: string) => ['wizard-drafts', 'list', userId] as const,
  detail: (draftId: string) => ['wizard-drafts', 'detail', draftId] as const,
};

// ── Helpers ───────────────────────────────────────────────────────────

function computeProgress(completedSteps: string[], skippedSteps: string[]): number {
  const total = completedSteps.length + skippedSteps.length;
  return Math.min(Math.round((total / 7) * 100), 99);
}

/** Strip unserializable fields before saving to DB. */
function stripWizardState(state: WizardState): Record<string, unknown> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { selectedGooglePlace, onAllCorrectClick, ...rest } = state;
  return rest as Record<string, unknown>;
}

/** Hydrate wizard state from DB — nullify unserializable fields. */
function hydrateWizardState(raw: Record<string, unknown>): WizardState {
  return {
    ...raw,
    selectedGooglePlace: null,
    onAllCorrectClick: null,
  } as WizardState;
}

function rowToSummary(row: WizardDraftRow): DraftSummary {
  return {
    draftId: row.id,
    displayName: row.display_name,
    progress: computeProgress(row.completed_steps, row.skipped_steps),
    lastUpdated: row.updated_at,
  };
}

// ── useWizardDrafts ───────────────────────────────────────────────────

export function useWizardDrafts(userId: string | undefined) {
  const { data: rows, ...rest } = useQuery({
    queryKey: wizardDraftKeys.lists(userId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('business_wizard_drafts')
        .select('id, user_id, display_name, current_step_id, setup_method, completed_steps, skipped_steps, wizard_state, version, created_at, updated_at, expires_at')
        .eq('user_id', userId!)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as WizardDraftRow[];
    },
    enabled: !!userId,
    staleTime: 30_000,
  });

  const summaries = useMemo<DraftSummary[]>(
    () => (rows ?? []).map(rowToSummary),
    [rows],
  );

  return { rows: rows ?? [], summaries, ...rest };
}

// ── useWizardDraftDetail ──────────────────────────────────────────────

export function useWizardDraftDetail(draftId: string | undefined) {
  return useQuery({
    queryKey: wizardDraftKeys.detail(draftId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('business_wizard_drafts')
        .select('id, user_id, display_name, current_step_id, setup_method, completed_steps, skipped_steps, wizard_state, version, created_at, updated_at, expires_at')
        .eq('id', draftId!)
        .single();

      if (error) throw error;
      return data as WizardDraftRow;
    },
    enabled: !!draftId,
    staleTime: 30_000,
  });
}

// ── useWizardDraftMutations ───────────────────────────────────────────

export function useWizardDraftMutations(userId: string | undefined) {
  const queryClient = useQueryClient();

  const upsertDraft = useMutation({
    mutationFn: async (payload: UpsertDraftPayload) => {
      const { data, error } = await supabase
        .from('business_wizard_drafts')
        .upsert(
          {
            id: payload.id,
            user_id: userId!,
            display_name: payload.displayName,
            current_step_id: payload.currentStepId,
            setup_method: payload.setupMethod,
            completed_steps: payload.completedSteps,
            skipped_steps: payload.skippedSteps,
            wizard_state: stripWizardState(payload.wizardState),
            version: 1,
          },
          { onConflict: 'id' },
        )
        .select('id')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: wizardDraftKeys.lists(userId) });
      }
    },
  });

  const removeDraft = useMutation({
    mutationFn: async (draftId: string) => {
      const { error } = await supabase
        .from('business_wizard_drafts')
        .delete()
        .eq('id', draftId);

      if (error) throw error;
    },
    // Optimistic delete
    onMutate: async (draftId: string) => {
      if (!userId) return;
      const listKey = wizardDraftKeys.lists(userId);
      await queryClient.cancelQueries({ queryKey: listKey });

      const previous = queryClient.getQueryData<WizardDraftRow[]>(listKey);
      queryClient.setQueryData<WizardDraftRow[]>(listKey, (old) =>
        (old ?? []).filter((row) => row.id !== draftId),
      );

      return { previous };
    },
    onError: (_err, _draftId, context) => {
      if (userId && context?.previous) {
        queryClient.setQueryData(wizardDraftKeys.lists(userId), context.previous);
      }
    },
    onSettled: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: wizardDraftKeys.lists(userId) });
      }
    },
  });

  return { upsertDraft, removeDraft };
}

// ── useMigrateLocalDrafts ─────────────────────────────────────────────

/**
 * One-time migration effect: reads localStorage drafts, inserts them to DB,
 * then removes localStorage keys. Guarded by a persistent flag.
 */
export function useMigrateLocalDrafts(userId: string | undefined) {
  const { upsertDraft } = useWizardDraftMutations(userId);
  const migrated = useRef(false);

  useEffect(() => {
    if (!userId || migrated.current) return;

    const flagKey = `taginn:drafts_migrated_${userId}`;
    if (localStorage.getItem(flagKey)) {
      migrated.current = true;
      return;
    }

    const storeKey = `businessWizardDrafts_${userId}`;
    const legacyKey = `businessWizardDraft_${userId}`;
    const raw = localStorage.getItem(storeKey);
    const legacyRaw = localStorage.getItem(legacyKey);

    if (!raw && !legacyRaw) {
      // Nothing to migrate
      localStorage.setItem(flagKey, '1');
      migrated.current = true;
      return;
    }

    (async () => {
      try {
        let drafts: Record<string, unknown> = {};

        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            drafts = parsed?.drafts ?? {};
          } catch { /* corrupt */ }
        }

        // Also migrate legacy single-draft
        if (legacyRaw && Object.keys(drafts).length === 0) {
          try {
            const legacy = JSON.parse(legacyRaw);
            if (legacy.version === 1) {
              const id = crypto.randomUUID();
              drafts[id] = { ...legacy, draftId: id, displayName: legacy.wizardState?.businessDetails?.name || null };
            }
          } catch { /* corrupt */ }
        }

        for (const [draftId, entry] of Object.entries(drafts)) {
          const d = entry as {
            draftId?: string;
            displayName?: string | null;
            currentStepId?: string;
            setupMethod?: string | null;
            completedSteps?: string[];
            skippedSteps?: string[];
            wizardState?: WizardState;
          };

          if (!d.currentStepId || !d.wizardState) continue;

          await upsertDraft.mutateAsync({
            id: d.draftId ?? draftId,
            displayName: d.displayName ?? null,
            currentStepId: d.currentStepId,
            setupMethod: d.setupMethod ?? null,
            completedSteps: d.completedSteps ?? [],
            skippedSteps: d.skippedSteps ?? [],
            wizardState: d.wizardState,
          });
        }

        // Cleanup localStorage
        localStorage.removeItem(storeKey);
        localStorage.removeItem(legacyKey);
        localStorage.setItem(flagKey, '1');
      } catch (err) {
        console.error('[useWizardDrafts] Migration failed:', err);
        // Don't set flag — will retry next session
      }
    })();

    migrated.current = true;
  }, [userId, upsertDraft]);
}

// ── Re-export hydrate helper for useWizardDraft ───────────────────────

export { hydrateWizardState };
export type { WizardDraftRow };
